import type { DecisionEngineChannelConfig } from "@hightouch/lib/customer-data/decision-engine/types";
import type { DecisionEngineDestinationUI } from "./definition";
import { accUI } from "./adobe-campaign-classic";
import { ajoUI } from "./adobe-experience-platform";
import { airshipUI } from "./airship";
import { attentiveUI } from "./attentive";
import { brazeCanvasUI, brazeTriggerUI } from "./braze";
import { cordialUI } from "./cordial";
import { eloquaCdoUI, eloquaTriggerUI } from "./eloqua";
import { iterableCampaignUI, iterableUI } from "./iterable";
import { klaviyoUI } from "./klaviyo";
import { marketoUI } from "./marketo";
import { moengageUI } from "./moengage";
import { mongodbUI } from "./mongodb";
import { oneSignalInAppUI, oneSignalUI } from "./one-signal";
import { sfmcDataExtensionsUI, sfmcJourneyUI, sfmcTriggerUI } from "./sfmc";

export type { DecisionEngineDestinationUI } from "./definition";

const DESTINATION_UI_REGISTRY: Record<string, DecisionEngineDestinationUI> = {
  iterable: iterableUI,
  "iterable:template": iterableUI,
  "iterable:campaign": iterableCampaignUI,
  cordial: cordialUI,
  braze: brazeTriggerUI,
  "braze:trigger": brazeTriggerUI,
  "braze:canvas": brazeCanvasUI,
  sfmc: sfmcTriggerUI,
  "sfmc:trigger": sfmcTriggerUI,
  eloqua: eloquaTriggerUI,
  "eloqua:trigger": eloquaTriggerUI,
  klaviyo: klaviyoUI,
  marketo: marketoUI,
  onesignal: oneSignalUI,
  "onesignal:inapp": oneSignalInAppUI,
  "adobe-campaign-classic": accUI,
  airship: airshipUI,
  attentive: attentiveUI,
  moengage: moengageUI,
  "adobe-experience-platform": ajoUI,
  "sfmc:journeyEntryEvent": sfmcJourneyUI,
  "sfmc:dataExtensions": sfmcDataExtensionsUI,
  "eloqua:customDataObject": eloquaCdoUI,
  mongodb: mongodbUI,
};

const defaultUI: DecisionEngineDestinationUI = {
  campaignSetup: "freeform",
  resourceName: "resource",
  channelDescription: "",
  getReferenceString: (s) => `{{ ${s} }}`,
  isPreviewSupported: false,
  isTestSendSupported: false,
};

export function getDestinationUI(
  destinationType: string,
  channelConfig?: DecisionEngineChannelConfig,
  channelType?: string,
): DecisionEngineDestinationUI {
  const compositeKeys = [
    channelConfig?.type ? `${destinationType}:${channelConfig.type}` : null,
    channelType ? `${destinationType}:${channelType}` : null,
  ];
  for (const key of compositeKeys) {
    const ui = key ? DESTINATION_UI_REGISTRY[key] : undefined;
    if (ui) {
      return ui;
    }
  }
  return DESTINATION_UI_REGISTRY[destinationType] ?? defaultUI;
}

/**
 * Resolve a field that may be a plain value or a function of channelConfig.
 * Used for `isTestSendSupported` and `showAssociatedTemplate` on SFMC DE.
 */
export function resolveUIField<T>(
  value: T | ((channelConfig: DecisionEngineChannelConfig) => T) | undefined,
  channelConfig: DecisionEngineChannelConfig,
  defaultValue: T,
): T {
  if (value === undefined) return defaultValue;
  if (typeof value === "function") {
    return (value as (c: DecisionEngineChannelConfig) => T)(channelConfig);
  }
  return value;
}
